import { useState, useEffect, Fragment } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Package, Wrench, Layers, FileText, Box, ArrowUpDown, Weight, Loader2, ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import api from '@/lib/api';

const STATUS_COLORS = {
  UNALLOCATED: { bg: '#ef444420', text: '#ef4444', label: 'Unallocated' },
  PARTIAL_SOFT: { bg: '#eab30820', text: '#eab308', label: 'Partial Soft' },
  READY_SOFT: { bg: '#3b82f620', text: '#3b82f6', label: 'Ready Soft' },
  PO_READY: { bg: '#143A6B20', text: '#143A6B', label: 'PO Ready' },
  PARTIAL_HARD: { bg: '#f59e0b20', text: '#f59e0b', label: 'Partial Hard' },
  FULLY_ALLOCATED: { bg: '#22c55e20', text: '#22c55e', label: 'Fully Allocated' },
};

const FAB_STAGES = ['fitUp', 'welding', 'ndt', 'painting', 'dispatch'];
const FAB_LABELS = { fitUp: 'Fit-Up', welding: 'Welding', ndt: 'NDT', painting: 'Painting', dispatch: 'Dispatch' };
const FAB_COLORS = { fitUp: '#3b82f6', welding: '#f59e0b', ndt: '#143A6B', painting: '#ec4899', dispatch: '#22c55e' };

function fmtNum(num) { return num != null ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 }).format(num) : '-'; }
function fmtPct(v) { return v != null ? `${Number(v).toFixed(0)}%` : '0%'; }

function SpoolDetailPanel({ projectId, lineId, onClose }) {
  const [spools, setSpools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/v1/projects/${projectId}/pwm/line-detail`, { params: { lineId } });
        setSpools(res.data?.spools || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [projectId, lineId]);

  if (loading) return (
    <tr><td colSpan={16} className="p-4">
      <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
    </td></tr>
  );

  return (
    <tr data-testid={`detail-panel-${lineId}`}>
      <td colSpan={16} style={{ padding: 0 }}>
        <div className="mx-4 my-2 rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: '#143A6B40' }}>
          <div className="flex items-center justify-between px-3 py-2 border-b" style={{ background: '#143A6B08', borderColor: '#143A6B20' }}>
            <span className="text-[10px] font-semibold flex items-center gap-1" style={{ color: '#143A6B' }}>
              <Package className="w-3 h-3" /> Spools for {lineId} ({spools.length})
            </span>
            <button onClick={onClose} className="p-1 rounded hover:bg-black/10" data-testid={`close-detail-${lineId}`}>
              <X className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>
          <div className="overflow-x-auto max-h-[320px]">
            <table className="w-full text-[10px]">
              <thead>
                <tr style={{ background: 'var(--card-bg-alt)' }}>
                  {['Spool #', 'Inch-Dia', 'Type', 'Alloc %', 'Current Stage', ...FAB_STAGES.map(s => FAB_LABELS[s]), 'Fab %'].map(h => (
                    <th key={h} className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {spools.map(sp => {
                  const allocPct = sp.overallAllocatedPercent || 0;
                  const sg = sp.stageGates || {};
                  const fabPct = sp.fabricationProgressPct || 0;
                  return (
                    <tr key={sp.spoolNumber} style={{ borderTop: '1px solid var(--border-color)' }} data-testid={`spool-row-${sp.spoolNumber}`}>
                      <td className="p-1.5 font-mono font-semibold" style={{ color: '#143A6B' }}>{sp.spoolNumber}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(sp.spoolInchDia)}</td>
                      <td className="p-1.5" style={{ color: 'var(--text-muted)' }}>{sp.spoolType || '-'}</td>
                      <td className="p-1.5 text-center"><PctBadge value={allocPct} /></td>
                      <td className="p-1.5 text-[9px]" style={{ color: 'var(--text-muted)' }}>{sp.currentStage?.replace(/_/g, ' ') || 'Not Started'}</td>
                      {FAB_STAGES.map(s => (
                        <td key={s} className="p-1.5 text-center" style={{ borderLeft: s === 'fitUp' ? '2px solid var(--border-color)' : undefined }}>
                          {sg[s]?.isComplete
                            ? <span style={{ color: '#22c55e' }}>{String(sg[s]?.date || '').slice(5) || 'Y'}</span>
                            : <span style={{ color: 'var(--text-disabled)' }}>-</span>}
                        </td>
                      ))}
                      <td className="p-1.5 text-center font-mono font-bold" style={{ color: '#143A6B', borderLeft: '2px solid var(--border-color)' }}>{fmtPct(fabPct)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function PWMSummaryDashboard({ projectId, filters = {} }) {
  const { isDark } = useTheme();
  const [summary, setSummary] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedLine, setExpandedLine] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.agUg) params.append('agUg', filters.agUg);
        if (filters.priority) params.append('priority', filters.priority);
        if (filters.area) params.append('area', filters.area);
        if (filters.status) params.append('status', filters.status);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const lineQs = params.toString() ? `${params.toString()}&limit=2000` : 'limit=2000';
        const [sumRes, lineRes] = await Promise.all([
          api.get(`/v1/projects/${projectId}/pwm/summary${qs}`),
          api.get(`/v1/projects/${projectId}/pwm/lines?${lineQs}`),
        ]);
        setSummary(sumRes.data);
        setLines(lineRes.data?.lines || []);
      } catch (err) { console.error('Failed to fetch PWM summary', err); }
      setLoading(false);
    })();
  }, [projectId, filters.agUg, filters.priority, filters.area, filters.status]);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="pwm-summary-loading">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#143A6B' }} />
    </div>
  );

  if (!summary || lines.length === 0) return (
    <div className="text-center py-20" data-testid="pwm-summary-empty">
      <Package className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>No PWM Data Yet</p>
      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Go to the Data Import tab to seed demo data or import Excel files.</p>
    </div>
  );

  const ag = summary.ag || {};
  const ug = summary.ug || {};
  const dist = summary.allocationStateDistribution || {};

  const chartData = [
    { name: 'Unallocated', count: dist.UNALLOCATED || 0, fill: '#ef4444' },
    { name: 'Partial Soft', count: dist.PARTIAL_SOFT || 0, fill: '#eab308' },
    { name: 'Ready Soft', count: dist.READY_SOFT || 0, fill: '#3b82f6' },
    { name: 'PO Ready', count: dist.PO_READY || 0, fill: '#143A6B' },
    { name: 'Partial Hard', count: dist.PARTIAL_HARD || 0, fill: '#f59e0b' },
    { name: 'Fully Allocated', count: dist.FULLY_ALLOCATED || 0, fill: '#22c55e' },
  ].filter(d => d.count > 0);
  const totalLines = chartData.reduce((s, d) => s + d.count, 0);

  const totalLineID = ag.totalLineInchDia + ug.totalLineInchDia;
  const totalSpoolID = ag.totalSpoolInchDia + ug.totalSpoolInchDia;
  const totalFabEarned = ag.fabricatedInchDia + ug.fabricatedInchDia;
  const totalDispatched = ag.dispatchedInchDia + ug.dispatchedInchDia;
  const totalErected = ag.erectedIM + ug.erectedIM;
  const totalIsoIM = ag.totalIsometricIM + ug.totalIsometricIM;

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sortedLines = [...lines].filter(l => {
    if (!search) return true;
    const s = search.toLowerCase();
    return l.lineId?.toLowerCase().includes(s) || l.area?.toLowerCase().includes(s);
  }).sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    const get = (row) => {
      switch (sortField) {
        case 'lineId': return row.lineId || '';
        case 'agUg': return row.agUg || '';
        case 'area': return row.area || '';
        case 'priority': return row.priority ?? 999;
        case 'totalId': return row.totalLineInchDia || 0;
        case 'softPct': return row.softAllocation?.allocationPercent || 0;
        case 'hardPct': return row.hardAllocation?.allocationPercent || 0;
        case 'fabPct': return row.fabricationProgress?.overallFabProgressPct || 0;
        case 'erecPct': return row.erectionProgress?.overallErectionProgressPct || 0;
        case 'status': return row.workfrontStatus || '';
        default: return '';
      }
    };
    const va = get(a), vb = get(b);
    if (typeof va === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb)) * dir;
  });

  const SortHeader = ({ field, label, style, className }) => (
    <th className={`p-2 font-medium cursor-pointer select-none hover:opacity-80 ${className || ''}`}
      style={style} onClick={() => handleSort(field)} data-testid={`sort-${field}`}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {sortField === field && <span className="text-[8px] ml-0.5">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
      </span>
    </th>
  );

  return (
    <div className="space-y-3" data-testid="pwm-summary-dashboard">
      {/* KPI Cards Row */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {[
          { label: 'Total Lines', value: summary.totalLines || 0, icon: Layers, color: '#143A6B' },
          { label: 'Total Spools', value: summary.totalSpools || 0, icon: Box, color: '#3B82F6' },
          { label: 'Line Inch-Dia', value: fmtNum(totalLineID), icon: Wrench, color: '#F59206' },
          { label: 'Fab Earned ID', value: fmtNum(totalFabEarned), icon: Layers, color: '#143A6B' },
          { label: 'Dispatched ID', value: fmtNum(totalDispatched), icon: Package, color: '#22c55e' },
          { label: 'Erected IM', value: fmtNum(totalErected), icon: Package, color: '#f59e0b' },
        ].map(card => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="p-2 rounded-lg" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid={`pwm-kpi-${card.label.replace(/\s/g, '-').toLowerCase()}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3.5 h-3.5" style={{ color: card.color }} />
                <span className="text-[9px] font-medium" style={{ color: 'var(--text-muted)' }}>{card.label}</span>
              </div>
              <p className="text-base font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{card.value}</p>
            </div>
          );
        })}
      </div>

      {/* State Distribution Bar Chart */}
      <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <p className="text-[10px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Allocation State Distribution ({totalLines} lines)</p>
        <div style={{ height: 140 }} data-testid="pwm-summary-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: -10, bottom: 4 }} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11 }}
                formatter={(value) => [`${value} lines (${totalLines > 0 ? ((value / totalLines) * 100).toFixed(1) : 0}%)`, 'Count']} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={50}>
                {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search line ID, area..."
          data-testid="pwm-summary-search"
          className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
      </div>

      {/* Line Matrix Table */}
      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-[11px]" data-testid="pwm-summary-table">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)', position: 'sticky', top: 0, zIndex: 2 }}>
              <th className="p-2 w-8" />
              <SortHeader field="lineId" label="Line ID" style={{ color: 'var(--text-muted)', minWidth: 120 }} className="text-left" />
              <SortHeader field="agUg" label="AG/UG" style={{ color: 'var(--text-muted)' }} className="text-center" />
              <SortHeader field="area" label="Area" style={{ color: 'var(--text-muted)' }} className="text-left" />
              <SortHeader field="priority" label="P" style={{ color: 'var(--text-muted)' }} className="text-center w-8" />
              <SortHeader field="totalId" label="Total ID" style={{ color: 'var(--text-muted)' }} className="text-right" />
              <SortHeader field="softPct" label="Soft %" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }} className="text-center" />
              <SortHeader field="hardPct" label="Hard %" style={{ color: '#22c55e' }} className="text-center" />
              <SortHeader field="fabPct" label="Fab %" style={{ color: '#143A6B' }} className="text-center" />
              <SortHeader field="erecPct" label="Erec %" style={{ color: '#f59e0b' }} className="text-center" />
              <SortHeader field="status" label="Status" style={{ color: 'var(--text-muted)' }} className="text-center" />
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)', borderLeft: '2px solid var(--border-color)' }}>Fab Loc</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Spools</th>
            </tr>
          </thead>
          <tbody>
            {sortedLines.map((line, idx) => {
              const wf = line.workfrontStatus || 'UNALLOCATED';
              const sc = STATUS_COLORS[wf] || STATUS_COLORS.UNALLOCATED;
              const softPct = line.softAllocation?.allocationPercent || 0;
              const hardPct = line.hardAllocation?.allocationPercent || 0;
              const fabPct = line.fabricationProgress?.overallFabProgressPct || 0;
              const erecPct = line.erectionProgress?.overallErectionProgressPct || 0;
              const isExpanded = expandedLine === line.lineId;
              const stripe = idx % 2 === 0 ? 'var(--card-bg)' : 'var(--card-bg-alt)';

              return (
                <Fragment key={line.lineId}>
                  <tr style={{ borderTop: '1px solid var(--border-color)', background: isExpanded ? '#143A6B08' : stripe }}
                    data-testid={`wf-line-${line.lineId}`}
                    className="cursor-pointer"
                    onClick={() => setExpandedLine(isExpanded ? null : line.lineId)}>
                    <td className="p-2">
                      {isExpanded
                        ? <ChevronDown className="w-3.5 h-3.5" style={{ color: '#143A6B' }} />
                        : <ChevronRight className="w-3.5 h-3.5" style={{ color: '#22c55e' }} />}
                    </td>
                    <td className="p-2 font-mono text-[10px] font-semibold" style={{ color: '#143A6B' }}>{line.lineId}</td>
                    <td className="p-2 text-center">
                      <span className="text-[8px] px-1.5 py-0.5 rounded" style={{ background: line.agUg === 'AG' ? '#3b82f620' : '#f59e0b20', color: line.agUg === 'AG' ? '#3b82f6' : '#f59e0b' }}>{line.agUg}</span>
                    </td>
                    <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>{line.area}</td>
                    <td className="p-2 text-center">
                      {line.priority ? <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: `${['', '#ef4444', '#f59e0b', '#3b82f6', '#143A6B', '#94a3b8'][line.priority]}20`, color: ['', '#ef4444', '#f59e0b', '#3b82f6', '#143A6B', '#94a3b8'][line.priority] }}>P{line.priority}</span> : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                    </td>
                    <td className="p-2 text-right font-mono text-[10px]" style={{ color: 'var(--text-primary)' }}>{fmtNum(line.totalLineInchDia)}</td>
                    <td className="p-2 text-center" style={{ borderLeft: '2px solid var(--border-color)' }}><MiniBar value={softPct} color="#3b82f6" /></td>
                    <td className="p-2 text-center"><MiniBar value={hardPct} color="#22c55e" /></td>
                    <td className="p-2 text-center"><MiniBar value={fabPct} color="#143A6B" /></td>
                    <td className="p-2 text-center"><MiniBar value={erecPct} color="#f59e0b" /></td>
                    <td className="p-2 text-center"><span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ background: sc.bg, color: sc.text }}>{sc.label}</span></td>
                    <td className="p-2 text-center text-[9px]" style={{ borderLeft: '2px solid var(--border-color)', color: 'var(--text-muted)' }}>{line.fabricationLocation || '-'}</td>
                    <td className="p-2 text-center">
                      <button onClick={e => { e.stopPropagation(); setExpandedLine(isExpanded ? null : line.lineId); }}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors hover:opacity-80"
                        style={{ background: '#143A6B15', color: '#143A6B', border: '1px solid #143A6B30' }}
                        data-testid={`ref-spools-${line.lineId}`}>
                        <Box className="w-2.5 h-2.5" /> View
                      </button>
                    </td>
                  </tr>
                  {isExpanded && <SpoolDetailPanel projectId={projectId} lineId={line.lineId} onClose={() => setExpandedLine(null)} />}
                </Fragment>
              );
            })}
            {sortedLines.length === 0 && <tr><td colSpan={16} className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No lines found. Seed data from Data Import tab.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MiniBar({ value, color }) {
  const v = Math.min(100, Math.max(0, value || 0));
  return (
    <div className="flex items-center gap-1 justify-center">
      <div className="w-10 h-1.5 rounded-full overflow-hidden" style={{ background: `${color}15` }}>
        <div className="h-full rounded-full" style={{ width: `${v}%`, background: color }} />
      </div>
      <span className="text-[9px] font-mono font-bold" style={{ color }}>{v.toFixed(0)}%</span>
    </div>
  );
}

function PctBadge({ value }) {
  const v = value || 0;
  return (
    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{
      background: v >= 100 ? '#22c55e20' : v > 0 ? '#f59e0b20' : '#94a3b820',
      color: v >= 100 ? '#22c55e' : v > 0 ? '#f59e0b' : '#94a3b8',
    }}>{fmtPct(v)}</span>
  );
}
